import { AxiosResponse } from "axios";
import { ButtonText } from "components/Atoms/Button";
import { SearchInput } from "components/Atoms/SearchInput";
import { LinkedPanelMessage } from "components/linkedBookkeepings/LinkedPanelMessage";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { LinkedBookkeepingPanelInstructionBase } from "components/Picker/Bookkeeping/model";
import { BookkeepingSmallDetails } from "components/SmallDetails";
import Table from "components/Tables/Table";
import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";

interface Props<T extends LinkedBookkeepingPanelInstructionBase> {
  queryKey: string;
  perPage: number;
  title: string;
  noSelectionLabel: string;
  listFunc: (instruction: T) => Promise<AxiosResponse<server.dto.ApiResponseWrapperWithRequest<server.dto.BookkeepingSmall[], server.dto.ListLinkedBookkeepingFilters>, any>>;
  initialInstruction: T;
  onDelete: (bookkeeping: server.dto.BookkeepingSmall) => void;
  onRemoveAll?: () => Promise<AxiosResponse>;
  onAddAll?: () => Promise<AxiosResponse>;
  onAdd: () => void;
}

export const LinkedBookkeepingsPanel = <T extends LinkedBookkeepingPanelInstructionBase>(props: Props<T>) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [total, setTotal] = React.useState<number>(0);
  const [selected, setSelected] = React.useState<number>(0);
  const [isInitialLoading, setIsIintialLoading] = useState<boolean>(true);
  const [isRemovingAll, setIsRemovingAll] = useState<boolean>(false);
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [instruction, setInstruction] = useState<T>({
    page: 1,
    perPage: props.perPage,
    ...props.initialInstruction,
  });

  React.useEffect(() => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: 1,
    });
  }, [debouncedSearchQuery]);

  const api = useCustomQuery({
    queryFn: () => props.listFunc(instruction),
    queryKey: ["bookkeepings", instruction, props.queryKey],
    onSuccess: (response) => {
      setTotal(response.data.absoluteCount);
      setSelected(response.data.selectedCount);
      setIsIintialLoading(false);
      pagination.setPagination(response.data.pagination);
    }
  });

  const onRemoveAll = async () => {
    setIsRemovingAll(true);
    await props.onRemoveAll?.();
    setIsRemovingAll(false);
    api.refetch();
  }

  const onAddAll = async () => {
    await props.onAddAll?.();
    api.refetch();
  }

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, props.perPage, paginate);
  const items = api.data?.data?.data || [];
  const displaySearchBar = selected > props.perPage;
  const displayRemoveAll = props.onRemoveAll && selected > 4;
  const isLoading = api.isLoading;

  return <SectionPanel.Panel
    header={<>
      <PanelSegment.Title title={props.title} />
      {selected < total && <PanelSegment.Button
        label={RESX.Buttons.Add}
        type="secondary"
        onClick={props.onAdd}
      />}
    </>}
  >
    <div className="flex-column gap">
      {!isInitialLoading && <LinkedPanelMessage
        selected={selected}
        total={total}
        nothingSelectedMessage={props.noSelectionLabel}
        entityName={RESX.Bookkeeping.Bookkeepings}
        onAddAll={props.onAddAll ? onAddAll : undefined}
      />}
      {displaySearchBar && <div>
        <SearchInput
          onChange={value => { setSearchQuery(value); }}
          value={searchQuery}
          placeholder={RESX.Company.SearchByName}
        />
      </div>}
      {pagination && <div className="flex justify-end">
        <PanelSegment.Pagination>{pagination.Component}</PanelSegment.Pagination>
      </div>}
      <Table
        variant="cards"
        hideNoDataAvailableDiv
        canDelete={() => true}
        onDelete={props.onDelete}
        items={items}
        isLoading={isLoading}
        getKey={bookkeeping => bookkeeping.bookkeepingId.toString()}
        hideHeader
        columns={[{
          key: "name",
          header: "",
          render: (bookkeeping) => <BookkeepingSmallDetails
            bookkeeping={bookkeeping}
          />,
          size: "1fr"
        }]}
      />
    </div>
    {displayRemoveAll && <div className="flex justify-end margin-top">
      <ButtonText
        className="no-padding"
        label={RESX.Buttons.RemoveAll}
        onClick={onRemoveAll}
        loading={isRemovingAll}
      />
    </div>}
  </SectionPanel.Panel>
}
